import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  size?: 'normal' | 'large';
}

export default function ComicCard({ comic, size = 'normal' }: ComicCardProps) {
  const formatViews = (views: number): string => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  const statusColors: Record<string, string> = {
    '连载中': 'bg-accent-100 text-accent-700',
    '已完结': 'bg-secondary-100 text-secondary-700',
  };

  const coverHeight = size === 'large' ? 'h-[340px] sm:h-[380px]' : 'h-[260px] sm:h-[280px]';

  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block"
      data-product-shop
    >
      <div className="relative overflow-hidden rounded-lg mb-3">
        <img
          src={comic.coverImage}
          alt={`${comic.title} 韩国漫画免费阅读`}
          title={`${comic.title} - 香香漫画韩国漫画免费阅读`}
          className={`w-full ${coverHeight} object-cover object-top transition-transform duration-500 group-hover:scale-105`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Status Badge */}
        <span
          className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${statusColors[comic.status] || 'bg-secondary-100 text-secondary-700'}`}
        >
          {comic.status}
        </span>

        {/* Hot/New Badge */}
        {comic.isHot && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
            HOT
          </span>
        )}
        {comic.isNew && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
            NEW
          </span>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
            立即阅读
          </span>
        </div>
      </div>

      <h3 className="text-sm md:text-[15px] font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1 mb-1.5">
        {comic.title}
      </h3>

      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <span>{comic.author}</span>
        <span className="text-foreground-300">·</span>
        <span className="flex items-center gap-1">
          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
          {comic.rating}
        </span>
        <span className="text-foreground-300">·</span>
        <span>{formatViews(comic.views)}阅读</span>
      </div>

      {size === 'large' && (
        <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
          {comic.description}
        </p>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {comic.tags.slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="px-2 py-0.5 text-[11px] text-foreground-600 bg-background-100 rounded-full whitespace-nowrap"
          >
            {tag}
          </span>
        ))}
      </div>
    </Link>
  );
}